import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Clock, Newspaper, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ContactForm from '../ContactForm';
import { items as blogItems, topics as blogTopics } from '../config/blog';
import LanguageSwitcher from '../components/LanguageSwitcher';

const PageHeader = ({ onOpenPopup, t }) => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
      <a href="/" className="flex items-center">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders';
          }}
        />
      </a>
      <div className="flex items-center gap-4 md:gap-8">
        <LanguageSwitcher variant="compact" />
        <button
          onClick={onOpenPopup}
          className="bg-[#141414] text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-semibold"
        >
          {t('common.free_diagnostic')}
        </button>
      </div>
    </div>
  </header>
);

const BlogPage = () => {
  const { t, i18n } = useTranslation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupContent, setPopupContent] = useState({ title: '', desc: '' });
  const [formConfig, setFormConfig] = useState({ scriptSrc: '', formData: '', formId: '' });
  const [activeTopic, setActiveTopic] = useState(null);
  const [now, setNow] = useState(() => new Date());
  const localeMap = { es: 'es-ES', en: 'en-US', pt: 'pt-PT' };
  const locale = localeMap[i18n.resolvedLanguage] || localeMap.es;

  const closePopup = () => setIsPopupOpen(false);

  const openContactPopup = () => {
    setIsPopupOpen(true);
    setPopupContent({
      title: t('common.diagnostic_request'),
      desc: t('common.diagnostic_desc'),
    });
    setFormConfig({
      scriptSrc: 'https://cdn.bitrix24.es/b24284919/crm/form/loader_4.js',
      formData: 'inline/4/10oz6w',
      formId: '4',
    });
  };

  const openSubscribePopup = () => {
    setIsPopupOpen(true);
    setPopupContent({
      title: t('blog.popup.subscribe_title'),
      desc: t('common.diagnostic_desc'),
    });
    setFormConfig({
      scriptSrc: 'https://cdn.bitrix24.es/b24284919/crm/form/loader_64.js',
      formData: 'inline/64/wh0nqu',
      formId: '64',
    });
  };

  useEffect(() => {
    if (!isPopupOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closePopup();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isPopupOpen]);

  useEffect(() => {
    if (!isPopupOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isPopupOpen]);

  useEffect(() => {
    const intervalId = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  const dateString = now.toLocaleDateString(locale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const timeString = now.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const heroMonth = now.toLocaleDateString(locale, { month: 'long' });

  const localizedTopics = useMemo(() => {
    const translated = t('blog_content.topics', { returnObjects: true });
    if (!Array.isArray(translated) || translated.length !== blogTopics.length) return blogTopics;
    return translated;
  }, [t]);

  const topicLabelByRaw = useMemo(() => {
    const map = new Map();
    blogTopics.forEach((rawTopic, index) => {
      map.set(rawTopic, localizedTopics[index] || rawTopic);
    });
    return map;
  }, [localizedTopics]);

  const getTopicLabel = (rawTopic) => topicLabelByRaw.get(rawTopic) || rawTopic;

  const localizedItems = useMemo(() => {
    const translated = t('blog_content.items', { returnObjects: true });
    if (!Array.isArray(translated)) return blogItems;

    const translatedByRoute = new Map(
      translated
        .filter((entry) => entry && typeof entry.route === 'string')
        .map((entry) => [entry.route, entry])
    );

    return blogItems.map((item) => {
      const itemTranslation = translatedByRoute.get(item.route);
      if (!itemTranslation) return item;
      return {
        ...item,
        title: itemTranslation.title || item.title,
        subtitle: itemTranslation.subtitle || item.subtitle
      };
    });
  }, [t]);

  const orderedItems = useMemo(() => {
    if (!activeTopic) return localizedItems;
    const matching = localizedItems.filter((it) => (it.topics || []).includes(activeTopic));
    const rest = localizedItems.filter((it) => !(it.topics || []).includes(activeTopic));
    return [...matching, ...rest];
  }, [activeTopic, localizedItems]);

  const headerStory = orderedItems.find((it) => it.route === '/crm') ?? orderedItems[0] ?? localizedItems[0];
  const headerStoryImage = '/images/blog/blog_renacimiento.png';

  return (
    <div className="min-h-screen bg-[#f7f7f5]">
      <PageHeader onOpenPopup={openContactPopup} t={t} />
      <main className="pt-20">
        <section className="border-y border-gray-300 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between py-3 text-gray-700">
              <div className="flex items-center gap-2">
                <Newspaper className="w-5 h-5" />
                <span className="uppercase tracking-widest text-xs">{t('blog.header.blog_label')}</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4" />{dateString}</span>
                <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{timeString}</span>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="bg-white border border-gray-300">
            <div className="px-6 py-4 border-b border-gray-300 flex items-center justify-between">
              <div className="font-serif text-2xl md:text-3xl font-extrabold tracking-tight text-[#141414]">
                {t('blog.header.main_title', { defaultValue: 'El Renacer de Tu Negocio' })}
              </div>
              <div className="text-xs font-semibold uppercase tracking-widest text-gray-600">
                  {activeTopic ? `${t('blog.header.topic_prefix')} ${getTopicLabel(activeTopic)}` : t('blog.header.general_edition')}
              </div>
            </div>
            <div className="relative min-h-[320px] md:min-h-[520px] border-b border-gray-300 bg-gray-100 overflow-hidden">
              <img
                src={headerStoryImage}
                alt={headerStory?.title || 'Portada'}
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://placehold.co/2400x1350/f0f0f0/171717?text=Portada';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/35 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                <div className="text-xs font-semibold text-white/85 uppercase tracking-widest">
                  {heroMonth}
                </div>
                <div className="mt-2 font-serif text-2xl md:text-5xl font-bold text-white leading-[1.08] max-w-5xl">
                  {t('blog.hero.title')}
                </div>
                <div className="mt-2 text-sm md:text-lg text-white/85 max-w-3xl">
                  {t('blog.hero.subtitle')}
                </div>
                <div className="mt-6">
                  <Link
                    to="/renacimiento"
                    className="inline-flex items-center justify-center px-6 py-2.5 rounded-md text-sm font-semibold bg-white text-[#141414] hover:bg-gray-100 transition"
                  >
                    {t('blog.hero.more_info')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-10">
          <div className="px-2 py-2">
            <div className="flex flex-wrap gap-2 justify-center">
              {blogTopics.map((topicRaw) => (
                <button
                  key={topicRaw}
                  onClick={() => setActiveTopic(activeTopic === topicRaw ? null : topicRaw)}
                  className={`px-3 py-1 text-xs rounded border ${
                    activeTopic === topicRaw ? 'bg-[#141414] text-white border-[#141414]' : 'bg-white text-gray-700 border-gray-300'
                  }`}
                >
                  {getTopicLabel(topicRaw)}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-14">
          <div className="grid lg:grid-cols-12 gap-6">
            {orderedItems.map((it, idx) => {
              const isTop = idx === 0 || idx === 1;
              const isWide = it.route === '/crm';
              const isHalfWidth = it.route === '/reclutamiento-de-personal' || it.route === '/clinicas';
              const colSpanClass = isTop
                ? 'col-span-12 lg:col-span-6'
                : isHalfWidth
                  ? 'col-span-12 lg:col-span-6'
                : isWide
                  ? 'col-span-12 lg:col-span-8'
                  : 'col-span-12 sm:col-span-6 lg:col-span-4';

              const imageClass = isTop || isHalfWidth ? 'h-56 md:h-64' : 'h-44';

              return (
                <article key={it.route} className={`bg-white border border-gray-200 overflow-hidden ${colSpanClass}`}>
                  {isWide ? (
                    <Link
                      to={it.route}
                      className="group relative block h-64 md:h-80 lg:h-96 bg-gray-100"
                    >
                      <img
                        src={it.image}
                        alt={it.title}
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://placehold.co/1200x675/f0f0f0/171717?text=Imagen';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-black/10" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

                      <div className="relative z-10 h-full p-6 md:p-10 flex flex-col justify-end">
                        <div className="text-xs font-semibold text-white/85 uppercase tracking-widest">
                          {getTopicLabel((it.topics || [])[0] || '')}
                        </div>
                        <h2 className="mt-2 font-serif text-3xl md:text-4xl font-bold text-white leading-[1.12]">
                          {it.title}
                        </h2>
                        <p className="mt-3 text-white/85 max-w-2xl">
                          {it.subtitle}
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2">
                          {(it.topics || []).slice(0, 3).map((topicRaw) => (
                            <span
                              key={topicRaw}
                              className="text-xs font-semibold bg-white/15 text-white px-2 py-1 rounded border border-white/15"
                            >
                              {getTopicLabel(topicRaw)}
                            </span>
                          ))}
                        </div>
                        <div className="mt-6">
                          <span className="inline-flex items-center justify-center px-5 py-2 rounded-md text-sm font-semibold bg-white text-[#141414] group-hover:bg-gray-100 transition">
                            {t('blog.card.read_now')}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ) : (
                    <>
                      <Link to={it.route} className="block bg-gray-100 border-b border-gray-200">
                        <img
                          src={idx === 0 ? it.heroImage ?? it.image : it.image}
                          alt={it.title}
                          className={`w-full object-cover ${imageClass}`}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://placehold.co/1200x675/f0f0f0/171717?text=Imagen';
                          }}
                        />
                      </Link>
                      <div className="p-6">
                        <div className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                          {getTopicLabel((it.topics || [])[0] || '')}
                        </div>
                        <h2 className={`mt-2 font-serif font-bold text-[#141414] leading-snug ${isTop ? 'text-2xl md:text-3xl' : 'text-xl'}`}>
                          <Link to={it.route}>{it.title}</Link>
                        </h2>
                        <p className="mt-3 text-gray-700">{it.subtitle}</p>
                        <div className="mt-4 flex flex-wrap gap-2">
                          {(it.topics || []).slice(0, 3).map((topicRaw) => (
                            <span key={topicRaw} className="text-xs font-semibold bg-gray-100 text-gray-700 px-2 py-1 rounded">
                              {getTopicLabel(topicRaw)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </article>
              );
            })}

            <div className="col-span-12 bg-white border border-gray-200 p-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="font-serif text-xl md:text-2xl font-bold text-[#141414]">{t('blog.subscribe.title')}</h3>
                  <p className="mt-1 text-gray-700">{t('blog.subscribe.desc')}</p>
                </div>
                <button
                  onClick={openSubscribePopup}
                  className="bg-[#141414] text-white px-5 py-2 rounded-md text-sm font-semibold hover:bg-gray-800 transition"
                >
                  {t('blog.subscribe.button')}
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {isPopupOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePopup();
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative transform transition-all duration-300 scale-100">
            <button
              type="button"
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition"
              aria-label={t('common.back')}
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#141414] mb-3">{popupContent.title}</h3>
              {popupContent.desc ? <p className="text-gray-600 mb-6">{popupContent.desc}</p> : null}
              <ContactForm
                scriptSrc={formConfig.scriptSrc}
                formData={formConfig.formData}
                formId={formConfig.formId}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogPage;

